import { useState } from "react"
import styles from "./updateUser.module.scss"
import ModalHeader from "../shared/modalHeader"
import ModalFooter from "../shared/modalFooter"
import { UpdateUserFormI } from "./types"
import { useMutation } from "@apollo/client"
import { UPDATE_USERS_ADMIN } from "../../graphql/mutations/update_users"
import Alert from "../shared/alert"
import { ACTIVATE_USER } from "../../graphql/mutations/activate_users"
import { DEACTIVATE_USER } from "../../graphql/mutations/deactivate_users"
import { TrexNavigator } from "../../classes/navigator/navigator"
import { USERS_UPDATE_ORG_MEMBERSHIP } from "../../graphql/mutations/user_organization_membership_update"
import { GET_USERS } from "../../graphql/queries/users"
import { useLocation, useNavigate } from "react-router-dom"
import Form from "./form"
import { useUpdateUserForm } from "./hook"
import {
    getFormMutationInputs,
    isSubmitFormDisabled,
    sanitizeForm,
} from "./api"
import { isFormValid } from "./validation"

interface UpdateUserModalContentPropsInterface {
    initialForm: UpdateUserFormI
    email: string
    organizationName: string
    subjectID: string
}

const UpdateUserFormContainer = ({
    initialForm,
    email,
    organizationName,
    subjectID,
}: UpdateUserModalContentPropsInterface) => {
    const navigate = useNavigate()
    const location = useLocation()

    // form state, form errors, form handlers
    const {
        form,
        formErrors,
        handleFirstNameChange,
        handleLastNameChange,
        handleUserRoleChange,
        handleActiveUserChange,
        handleFormBlur,
    } = useUpdateUserForm(initialForm)

    // error banner
    const [updateUserErrorBanner, setUpdateUserErrorBanner] = useState(false)

    // mutations
    const [updateUserMutation, { loading: updateUsrLoading }] =
        useMutation(UPDATE_USERS_ADMIN)

    const [
        usersUpdateOrganizationMemberships,
        { loading: updateOrgMemLoading },
    ] = useMutation(USERS_UPDATE_ORG_MEMBERSHIP)

    const [activateUser, { loading: activateUsrLoading }] = useMutation(
        ACTIVATE_USER,
        { refetchQueries: [GET_USERS] }
    )

    const [deactivateUser, { loading: deactivateUsrLoading }] = useMutation(
        DEACTIVATE_USER,
        { refetchQueries: [GET_USERS] }
    )

    const handleSubmitUpdateUser = async () => {
        if (!isFormValid(form)) {
            console.error("Form is invalid and cannot be submitted")
            return
        }

        const sanitizedForm = sanitizeForm(form)

        const {
            usersActivateInput,
            usersDeactivateInput,
            usersUpdateAdminInput,
            usersUpdateOrgMemInput,
        } = getFormMutationInputs(initialForm, sanitizedForm, subjectID)

        const hasActivationChange =
            !!usersActivateInput || !!usersDeactivateInput

        try {
            const mutationPromises = []

            // Only allow activation/deactivation *or* other updates, never both
            if (hasActivationChange) {
                if (usersActivateInput) {
                    mutationPromises.push(
                        activateUser({
                            variables: {
                                UsersActivateInput: usersActivateInput,
                            },
                        })
                    )
                } else if (usersDeactivateInput) {
                    mutationPromises.push(
                        deactivateUser({
                            variables: {
                                UsersDeactivateInput: usersDeactivateInput,
                            },
                        })
                    )
                }
            } else {
                if (usersUpdateAdminInput) {
                    mutationPromises.push(
                        updateUserMutation({
                            variables: {
                                UsersUpdateAdmin: usersUpdateAdminInput,
                            },
                        })
                    )
                }
                if (usersUpdateOrgMemInput) {
                    mutationPromises.push(
                        usersUpdateOrganizationMemberships({
                            variables: {
                                UsersUpdateOrganizationMembershipInput:
                                    usersUpdateOrgMemInput,
                            },
                        })
                    )
                }
            }

            await Promise.all(mutationPromises)
            navigate(-1)
        } catch (error) {
            console.error("Update user modal mutation error", error)
            setUpdateUserErrorBanner(true)
        }
    }

    const isLoading =
        updateUsrLoading ||
        updateOrgMemLoading ||
        activateUsrLoading ||
        deactivateUsrLoading
    const hasError = updateUserErrorBanner

    const disableSubmitUpdateUser = isSubmitFormDisabled(
        initialForm,
        form,
        hasError,
        isLoading
    )

    return (
        <div className={styles.container}>
            <ModalHeader
                title="Update User"
                trexNavigator={
                    new TrexNavigator(
                        {
                            navigateTo: -1,
                            locationKey: location.key,
                            pathName: location.pathname,
                        },
                        navigate
                    )
                }
            />
            <Form
                email={email}
                organizationName={organizationName}
                form={form}
                initialActivationStatus={initialForm.activeUser}
                formErrors={formErrors}
                handleFirstNameChange={handleFirstNameChange}
                handleLastNameChange={handleLastNameChange}
                handleUserRoleChange={handleUserRoleChange}
                handleActiveUserChange={handleActiveUserChange}
                handleFormBlur={handleFormBlur}
            />
            {updateUserErrorBanner && (
                <Alert
                    isCloseable={false}
                    message={
                        <div>
                            There was a problem updating the user. Click{" "}
                            <u
                                style={{ cursor: "pointer" }}
                                onClick={() => navigate("/admin/users")}
                            >
                                here
                            </u>{" "}
                            or close the modal to be redirected to the admin
                            page.
                        </div>
                    }
                    alertType="danger"
                />
            )}
            <ModalFooter
                advanceText="Save"
                handleAdvanceClick={handleSubmitUpdateUser}
                disableAdvance={disableSubmitUpdateUser}
            />
        </div>
    )
}

export default UpdateUserFormContainer
